import { useEffect, useState } from "react";
import { api, authedUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Upload, Download, FileText, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

/**
 * Reusable "Import CSV / Export CSV / Template" trio.
 *
 * Props:
 *  - entityLabel: human singular (e.g. "Epic")
 *  - entityPlural: human plural (e.g. "Epics")
 *  - exportUrl: GET endpoint for CSV (e.g. "/api/epics/export.csv")
 *  - templateUrl: GET endpoint for the blank template
 *  - importUrl: POST endpoint for the upload
 *  - columns: comma-space list of column names rendered in the dialog hint
 *  - onImported: callback when at least one row imported successfully
 *  - canImport: hide the import affordance for non-admins
 */
export default function BulkIO({
  entityLabel, entityPlural,
  exportUrl, templateUrl, importUrl,
  columns, onImported, canImport = true,
}) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => { if (open) { setFile(null); setResult(null); } }, [open]);

  const submit = async () => {
    if (!file) return toast.error("Pick a CSV file first");
    setUploading(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(importUrl, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      const created = data.summary?.created ?? data.created ?? 0;
      const failed = data.summary?.failed ?? data.failed ?? 0;
      if (created > 0) {
        toast.success(`Imported ${created} ${created === 1 ? entityLabel.toLowerCase() : entityPlural.toLowerCase()}${failed ? `, ${failed} failed` : ""}`);
        onImported?.();
      } else {
        toast.error(`No ${entityPlural.toLowerCase()} imported — ${failed} row${failed === 1 ? "" : "s"} failed`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally { setUploading(false); }
  };

  const results = result?.results || [];
  const summary = result?.summary || { created: result?.created || 0, failed: result?.failed || 0 };

  return (
    <>
      <Button asChild variant="outline" size="sm" data-testid={`${entityLabel.toLowerCase()}-export-btn`}>
        <a href={authedUrl(exportUrl)} rel="noopener">
          <Download className="w-4 h-4 mr-1" />Export CSV
        </a>
      </Button>
      {canImport && (
        <Button variant="outline" size="sm" onClick={() => setOpen(true)} data-testid={`${entityLabel.toLowerCase()}-import-btn`}>
          <Upload className="w-4 h-4 mr-1" />Import CSV
        </Button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid={`${entityLabel.toLowerCase()}-import-dialog`}>
          <DialogHeader>
            <DialogTitle>Import {entityPlural} from CSV</DialogTitle>
            <DialogDescription>
              Upload a CSV with the columns below. <code className="text-xs">name</code> and <code className="text-xs">project_code</code> are required.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <FileText className="w-3.5 h-3.5" />
              <span>Columns: {columns}</span>
            </div>
            <a
              data-testid={`${entityLabel.toLowerCase()}-import-template`}
              href={authedUrl(templateUrl)}
              className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline"
            >
              <Download className="w-3.5 h-3.5" />Download blank template
            </a>

            <label
              htmlFor={`import-file-${entityLabel.toLowerCase()}`}
              className="block cursor-pointer glass rounded-xl border-2 border-dashed border-border/60 hover:border-primary/60 transition p-6 text-center"
            >
              <Upload className="w-6 h-6 mx-auto mb-2 text-muted-foreground" />
              <div className="text-sm font-medium">{file ? file.name : "Click to choose a .csv file"}</div>
              <div className="text-[11px] text-muted-foreground mt-1">Max ~5MB · comma-separated</div>
              <input
                id={`import-file-${entityLabel.toLowerCase()}`}
                type="file" accept=".csv,text/csv" className="hidden"
                data-testid={`${entityLabel.toLowerCase()}-import-file`}
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
            </label>

            {result && (
              <div className="glass rounded-xl p-3 space-y-2" data-testid={`${entityLabel.toLowerCase()}-import-result`}>
                <div className="flex items-center gap-3 text-sm">
                  <span className="inline-flex items-center gap-1 text-emerald-500"><CheckCircle2 className="w-4 h-4" />{summary.created} created</span>
                  <span className="inline-flex items-center gap-1 text-red-500"><XCircle className="w-4 h-4" />{summary.failed} failed</span>
                </div>
                {results.length > 0 && (
                  <div className="max-h-56 overflow-y-auto text-[11px] space-y-0.5">
                    {results.map((r, i) => (
                      <div key={i} className={r.ok ? "text-muted-foreground" : "text-red-500"}>
                        <span className="font-mono">Row {r.row}:</span>{" "}
                        {r.ok ? <>✓ {r.name || r.title} <span className="text-emerald-500">{r.identifier || ""}</span></> : <>✗ {r.name || r.title || "—"}: {r.error}</>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={uploading}>Close</Button>
            <Button onClick={submit} disabled={!file || uploading} data-testid={`${entityLabel.toLowerCase()}-import-submit`}>
              {uploading ? "Uploading…" : "Upload & Import"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
